import styled, { keyframes, css } from "styled-components";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Page = styled.div`
  min-height: 100vh;
  background: var(--app-bg-gradient);
  color: var(--text-primary);
  font-family: "Inter", "Segoe UI", system-ui, sans-serif;
  overflow-x: hidden;
`;

/** Gradiente de marca B4/Vert — fixo nos dois temas. */
export const GradientText = styled.span`
  background: linear-gradient(135deg, #25d07f 0%, #10b7a6 45%, #4ed8ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

export const Nav = styled.nav`
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(16px);
  background: var(--surface-overlay);
  border-bottom: 1px solid var(--border-subtle);
`;

export const NavInner = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

export const NavBrand = styled.div`
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: -0.02em;
  color: var(--text-primary);
`;

export const NavAnchors = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 1rem;
  align-items: center;
  justify-content: flex-end;
`;

export const NavAnchor = styled.a`
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.8125rem;
  font-weight: 500;
  transition: color 0.2s ease;
  &:hover {
    color: var(--success);
  }
`;

export const Section = styled.section`
  max-width: 1120px;
  margin: 0 auto;
  padding: 4.5rem 1.5rem;
  animation: ${fadeUp} 0.6s ease both;
`;

export const SectionMuted = styled(Section)`
  background: var(--surface-glass);
  border-top: 1px solid var(--border-subtle);
  border-bottom: 1px solid var(--border-subtle);
  max-width: none;
  padding-left: max(1.5rem, calc(50vw - 560px));
  padding-right: max(1.5rem, calc(50vw - 560px));
`;

export const SectionInner = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

export const BenefitChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.75rem;

  @media (min-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
`;

export const BenefitChartCard = styled.div`
  background: var(--surface-glass);
  border: 1px solid var(--border-subtle);
  border-radius: 18px;
  padding: 1.5rem 1.25rem 1.25rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-height: 400px;
`;

export const BenefitChartHeader = styled.div`
  flex-shrink: 0;
  min-height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.35rem;
`;

export const BenefitChartTitle = styled.h3`
  margin: 0;
  text-align: center;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-heading);
  line-height: 1.35;
`;

export const BenefitChartFrame = styled.div`
  position: relative;
  flex: 1 1 auto;
  width: 100%;
  min-height: 330px;
  padding: 0.05rem 0.1rem 0.15rem;
`;

export const BenefitChartVisual = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 295px;
  max-height: 325px;
  margin: 0 auto;
  max-width: 285px;
`;

export const BenefitCornerValues = styled.div`
  position: absolute;
  bottom: 0.35rem;
  left: 0.35rem;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.4rem;
  max-width: 48%;
`;

export const BenefitCornerValue = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.45rem;
  text-align: left;
`;

/** $color vem dos dados do gráfico (fatia) — não segue o tema. */
export const BenefitCornerDot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  box-shadow: 0 0 0 2px var(--surface-glass-strong);
  flex-shrink: 0;
`;

export const BenefitCornerText = styled.span`
  color: ${({ $color }) => $color};
  font-size: clamp(0.82rem, 2.2vw, 1rem);
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.01em;
`;

export const BenefitChartCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  text-align: center;
  padding: 0 2.75rem;
  z-index: 1;

  strong {
    display: block;
    color: var(--text-primary);
    font-size: clamp(1.2rem, 2.8vw, 1.55rem);
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.02em;
  }

  span {
    display: block;
    margin-top: 0.3rem;
    color: var(--accent);
    font-size: 0.72rem;
    font-weight: 600;
    line-height: 1.35;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
`;

export const BenefitChartsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

export const BenefitSharedLegend = styled.ul`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 1.25rem 2.5rem;
  margin: 0;
  padding: 1rem 1.25rem;
  list-style: none;
  background: var(--surface-glass);
  border: 1px solid var(--border-subtle);
  border-radius: 14px;
`;

export const BenefitLegendItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.55rem;
`;

export const BenefitLegendItemHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
`;

/** $color vem dos dados do gráfico (fatia) — não segue o tema. */
export const BenefitLegendSwatch = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  box-shadow: 0 0 0 2px var(--surface-glass-strong);
  flex-shrink: 0;
`;

export const BenefitLegendName = styled.span`
  color: var(--text-heading);
  font-size: 0.9rem;
  font-weight: 600;
`;

export const Eyebrow = styled.p`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--accent);
  margin: 0 0 1rem 0;
  font-weight: 600;
`;

export const H1 = styled.h1`
  font-size: clamp(2.25rem, 5vw, 3.25rem);
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.03em;
  margin: 0 0 1.25rem 0;
  color: var(--text-primary);
`;

export const H2 = styled.h2`
  font-size: clamp(1.75rem, 3.5vw, 2.25rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0 0 1rem 0;
  color: var(--text-primary);
`;

export const Lead = styled.p`
  font-size: 1.125rem;
  line-height: 1.65;
  color: var(--text-secondary);
  margin: 0 0 2rem 0;
  max-width: 36rem;
`;

export const Body = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: var(--text-secondary);
  margin: 0 0 1rem 0;
`;

export const Hero = styled.section`
  position: relative;
  max-width: 1120px;
  margin: 0 auto;
  padding: 5rem 1.5rem 6rem;
  min-height: 72vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  &::before {
    content: "";
    position: absolute;
    inset: -20% -10% auto;
    height: 70%;
    background: radial-gradient(
      ellipse 80% 60% at 50% 0%,
      rgba(37, 208, 127, 0.12),
      transparent 70%
    );
    pointer-events: none;
    z-index: 0;
  }
  > * {
    position: relative;
    z-index: 1;
  }
`;

export const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem;
  align-items: center;
  @media (min-width: 900px) {
    grid-template-columns: 1.1fr 0.9fr;
  }
`;

export const HeroVisual = styled.div`
  border-radius: 24px;
  min-height: 220px;
  background: linear-gradient(
      145deg,
      rgba(37, 208, 127, 0.15) 0%,
      rgba(16, 183, 166, 0.1) 50%,
      rgba(78, 216, 255, 0.12) 100%
    ),
    var(--surface-1);
  border: 1px solid var(--border-default);
  box-shadow: var(--shadow-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  font-size: 0.9rem;
  color: var(--text-secondary);
  text-align: center;
`;

export const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.5rem;
`;

/** CTA principal usa o gradiente de marca B4/Vert — fixo nos dois temas. */
const btnPrimaryStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.95rem 1.75rem;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.9375rem;
  text-decoration: none;
  color: var(--text-on-accent);
  background: linear-gradient(135deg, #25d07f 0%, #10b7a6 45%, #4ed8ff 100%);
  box-shadow: var(--shadow-md);
  border: none;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  font-family: inherit;
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }
`;

export const BtnPrimary = styled.a`
  ${btnPrimaryStyles}
`;

export const BtnPrimaryNative = styled.button`
  ${btnPrimaryStyles}
`;

export const BtnGhost = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.95rem 1.75rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.9375rem;
  text-decoration: none;
  color: var(--text-primary);
  background: var(--surface-glass);
  border: 1px solid var(--border-default);
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
  &:hover {
    background: var(--accent-soft);
    border-color: var(--border-strong);
  }
`;

export const BtnLink = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--success);
  background: transparent;
  border: 1px solid var(--border-default);
  cursor: pointer;
  font-family: inherit;
  transition: background 0.2s ease;
  &:hover {
    background: var(--accent-soft);
  }
`;

export const TwoCol = styled.div`
  display: grid;
  gap: 2.5rem;
  align-items: center;
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const Glass = styled.div`
  background: var(--surface-glass);
  border: 1px solid var(--border-subtle);
  border-radius: 20px;
  padding: 2rem;
  box-shadow: var(--shadow-md);
`;

export const CardGrid = styled.div`
  display: grid;
  gap: 1.25rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const Card = styled.div`
  background: var(--surface-glass);
  border: 1px solid var(--border-subtle);
  border-radius: 16px;
  padding: 1.75rem;
  box-shadow: var(--shadow-sm);
  transition: border-color 0.2s ease, transform 0.2s ease;
  &:hover {
    border-color: var(--border-default);
    transform: translateY(-2px);
  }
`;

export const CardTitle = styled.h3`
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  color: var(--text-primary);
`;

export const CardText = styled.p`
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--text-secondary);
  margin: 0;
`;

export const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.25rem 1.75rem;
  align-items: stretch;
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2.25rem 1.75rem;
  }
  @media (min-width: 1000px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const ProjectCard = styled.article`
  background: var(--surface-glass);
  border: 1px solid var(--border-subtle);
  border-radius: 18px;
  padding: 2.6rem 1.9rem 2.85rem;
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
  min-height: 0;
  position: relative;
  z-index: 0;
  width: 100%;
  box-sizing: border-box;
  @media (max-width: 639px) {
    padding: 1.65rem 1.2rem 1.85rem;
    gap: 1.25rem;
  }
  @media (min-width: 640px) {
    min-height: 420px;
  }
  @media (min-width: 1000px) {
    min-height: 440px;
  }
  & p {
    line-height: 1.85;
  }
`;

/** Cards de aposentadoria — mesma base dos pacotes, com contraste sutil (faixa âmbar, fundo mais quente). */
export const RetirementCard = styled(ProjectCard)`
  height: 100%;
  gap: 0.85rem;
  padding: 1.65rem 1.45rem 1.75rem;
  border-color: var(--border-subtle);
  border-left: 3px solid var(--border-strong);
  background: linear-gradient(
      155deg,
      rgba(0, 176, 86, 0.07) 0%,
      rgba(219, 251, 142, 0.05) 100%
    ),
    var(--surface-glass);
  box-shadow:
    var(--shadow-md),
    inset 0 1px 0 var(--border-subtle);

  @media (min-width: 640px) {
    min-height: 520px;
  }

  @media (min-width: 1000px) {
    min-height: 540px;
  }
`;

export const RetirementCardHeader = styled.div`
  flex-shrink: 0;
  min-height: 6.25rem;
`;

export const RetirementCardTitle = styled(CardTitle)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  min-height: 2.75em;
  line-height: 1.35;
`;

export const RetirementCardBody = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  min-height: 0;
`;

export const RetirementPurposeSection = styled.div`
  flex: 1 1 auto;
  min-height: 5.25rem;
  display: flex;
  flex-direction: column;
`;

export const RetirementCardFooter = styled.div`
  flex-shrink: 0;
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  padding-top: 0.35rem;
`;

export const RetirementCardActionsSlot = styled.div`
  min-height: 5.65rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

/** Tag de status com verde-limão de marca — fixo nos dois temas. */
export const RetirementStatusTag = styled.span`
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-on-accent);
  background: linear-gradient(135deg, #dbfb8e 0%, #7ee8a8 100%);
  padding: 0.28rem 0.6rem;
  border-radius: 6px;
`;

export const RetirementBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.35rem;
`;

export const RetirementBadge = styled.span`
  font-size: 0.68rem;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  background: var(--surface-glass);
  border: 1px solid var(--border-subtle);
  color: var(--text-secondary);
`;

export const RetirementBadgeMuted = styled(RetirementBadge)`
  background: var(--accent-secondary-soft);
  border-color: var(--border-subtle);
  color: var(--text-secondary);
`;

export const RetirementMetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem 1rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const RetirementMetaItem = styled.div`
  min-width: 0;

  small {
    display: block;
    font-size: 0.68rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-muted);
    margin-bottom: 0.2rem;
  }

  p {
    margin: 0;
    font-size: 0.84rem;
    color: var(--text-secondary);
    line-height: 1.45;
    word-break: break-word;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    min-height: 2.9em;
  }
`;

export const RetirementPurpose = styled.p`
  margin: 0;
  font-size: 0.84rem;
  line-height: 1.55;
  color: var(--text-secondary);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${(p) => (p.$expanded ? "unset" : 3)};
  overflow: hidden;
  min-height: ${(p) => (p.$expanded ? "0" : "4.65em")};
`;

export const RetirementQuantity = styled.div`
  padding: 0.9rem 1rem;
  border-radius: 12px;
  text-align: center;
  background: var(--accent-soft);
  border: 1px solid var(--border-default);
  min-height: 4.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  strong {
    display: block;
    font-size: clamp(1.35rem, 2.5vw, 1.65rem);
    font-weight: 800;
    color: var(--text-heading);
    line-height: 1.2;
  }

  span {
    display: block;
    margin-top: 0.2rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
`;

/** Vitrine — lista de cards de projeto em destaque. */
export const FeaturedShowcaseStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-bottom: 2.5rem;
  width: 100%;
`;

/** Card horizontal único: texto (esq.) + certificado (dir.). */
export const FeaturedShowcaseCard = styled.article`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  column-gap: clamp(1.25rem, 3vw, 2rem);
  align-items: center;
  background: linear-gradient(
      135deg,
      rgba(0, 176, 86, 0.1) 0%,
      rgba(78, 216, 255, 0.06) 100%
    ),
    var(--surface-1);
  border: 1px solid var(--border-default);
  border-radius: 20px;
  padding: clamp(1.5rem, 2.5vw, 2.25rem) clamp(1.25rem, 2.5vw, 2rem);
  box-shadow:
    var(--shadow-md),
    0 0 0 1px var(--border-subtle) inset;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    row-gap: 1.5rem;
    padding: 1.5rem 1.25rem 1.65rem;
    justify-items: stretch;
  }
`;

export const FeaturedShowcaseCopyCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(1.1rem, 2vw, 1.5rem);
  min-width: 0;
  width: 100%;
  max-width: 34rem;
  margin: 0 auto;
  text-align: center;
  align-self: center;
`;

/** Coluna da imagem do certificado. */
export const FeaturedShowcaseHeroCol = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  width: 100%;
  @media (max-width: 767px) {
    order: -1;
    max-width: min(100%, 340px);
    margin: 0 auto;
  }
`;

/** Área clicável (lightbox) — fundo transparente, sem borda de sub-card. */
export const FeaturedShowcaseHeroFrame = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: clamp(280px, 42vh, 520px);
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: zoom-in;
  outline: none;
  border-radius: 6px;
  transition: opacity 0.18s ease;
  @media (max-width: 767px) {
    min-height: 0;
  }
  &:hover {
    opacity: 0.9;
  }
  &:focus-visible {
    box-shadow: 0 0 0 2px var(--success);
  }
`;

/** Certificado — maior em relação ao painel direito do card. */
export const FeaturedShowcaseHeroImg = styled.img`
  display: block;
  width: 100%;
  max-width: min(100%, 540px);
  height: auto;
  max-height: min(540px, 64vh);
  margin: 0 auto;
  object-fit: contain;
  pointer-events: none;
  filter: drop-shadow(0 14px 32px rgba(0, 0, 0, 0.38));
  @media (min-width: 768px) {
    max-width: min(100%, 580px);
    max-height: min(580px, 68vh);
  }
  @media (max-width: 767px) {
    max-width: min(100%, 400px);
    max-height: min(400px, 50vh);
  }
`;

export const FeaturedShowcaseSideStart = styled.div`
  justify-self: start;
  display: flex;
  width: 100%;
  min-width: 0;
  @media (max-width: 767px) {
    justify-content: center;
  }
`;

export const FeaturedShowcaseSideEnd = styled.div`
  justify-self: end;
  display: flex;
  width: 100%;
  min-width: 0;
  @media (max-width: 767px) {
    justify-content: center;
  }
`;

/** Moldura fixa — miniaturas com contain (imagem inteira no card; clique abre tamanho real). */
export const FeaturedShowcaseThumbFrame = styled.button`
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  border: none;
  width: var(--showcase-thumb);
  aspect-ratio: 4 / 5;
  flex-shrink: 0;
  border-radius: 12px;
  overflow: hidden;
  cursor: zoom-in;
  display: block;
  position: relative;
  background: var(--surface-glass);
  outline: none;
  transition: box-shadow 0.2s ease;
  &:focus-visible {
    box-shadow: 0 0 0 2px var(--success), 0 0 0 4px var(--surface-overlay);
  }
  &:hover {
    box-shadow: var(--shadow-md);
  }
`;

export const FeaturedShowcaseThumbImgCover = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  display: block;
  pointer-events: none;
`;

export const FeaturedShowcaseCenter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.25rem;
  min-width: 0;
  width: 100%;
  text-align: center;
`;

export const FeaturedShowcaseProtocolBand = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const FeaturedShowcaseProtocolImg = styled.img`
  width: auto;
  max-width: min(100%, 380px);
  max-height: clamp(72px, 13vw, 150px);
  height: auto;
  object-fit: contain;
  display: block;
  margin: 0 auto;
`;

export const FeaturedImageLightboxBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 12000;
  background: var(--surface-overlay);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: max(3.75rem, env(safe-area-inset-top, 0px))
    max(1rem, env(safe-area-inset-right, 0px))
    max(1rem, env(safe-area-inset-bottom, 0px))
    max(1rem, env(safe-area-inset-left, 0px));
  overflow: auto;
  overscroll-behavior: contain;
  box-sizing: border-box;
`;

export const FeaturedImageLightboxPanel = styled.div`
  flex: 0 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  max-width: min(96vw, 1100px);
`;

export const FeaturedImageLightboxImg = styled.img`
  display: block;
  max-width: min(96vw, 100%);
  max-height: min(calc(100vh - 5rem), calc(100dvh - 5rem), 92vh);
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 10px;
`;

export const FeaturedImageLightboxClose = styled.button`
  position: fixed;
  top: max(1rem, env(safe-area-inset-top, 0px));
  right: max(1rem, env(safe-area-inset-right, 0px));
  z-index: 12001;
  background: var(--accent-soft);
  border: 2px solid var(--accent);
  color: var(--accent-secondary);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: var(--accent);
    color: var(--text-on-accent);
  }
`;

const projectCardActionBtnStyles = css`
  width: 100%;
  min-width: 0;
  min-height: 2.5rem;
  padding: 0.5rem 0.5rem;
  font-size: clamp(0.65rem, 0.85vw, 0.75rem);
  line-height: 1.15;
  text-align: center;
  white-space: normal;
  word-break: break-word;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

/** Botões nos cards de pacotes — coluna com espaçamento. */
export const ProjectCardActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;
  margin-top: auto;
  padding-top: 1.1rem;
  width: 100%;
`;

export const RetirementCardActions = styled(ProjectCardActions)`
  padding-top: 0.5rem;
  gap: 0.55rem;
`;

export const B4PageToast = styled.div`
  position: fixed;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3000;
  width: min(92vw, 520px);
  padding: 1rem 1.15rem;
  border-radius: 14px;
  background: var(--surface-1);
  border: 1px solid var(--border-default);
  box-shadow: var(--shadow-lg);
  color: var(--text-heading);
  animation: b4toast-in 0.28s ease;

  @keyframes b4toast-in {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }

  strong {
    display: block;
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--success);
    margin-bottom: 0.35rem;
  }

  p {
    margin: 0;
    font-size: 0.88rem;
    line-height: 1.45;
    color: var(--text-secondary);
  }
`;

export const B4PageToastActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.85rem;
`;

export const B4PageToastButton = styled.button`
  appearance: none;
  border: 1px solid var(--border-default);
  background: var(--accent-soft);
  color: var(--accent-secondary);
  border-radius: 8px;
  padding: 0.45rem 0.9rem;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: var(--accent-secondary-soft);
  }
`;

export const ProjectCardActionGhost = styled(BtnGhost)`
  ${projectCardActionBtnStyles}
`;

export const ProjectCardActionPrimary = styled(BtnPrimaryNative)`
  ${projectCardActionBtnStyles}
`;

export const Tag = styled.span`
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--accent);
  margin-bottom: 0.25rem;
`;

export const Col3 = styled.div`
  display: grid;
  gap: 2rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const IconCol = styled.div`
  text-align: left;
  h3 {
    font-size: 1rem;
    font-weight: 700;
    color: var(--success);
    margin: 0 0 0.75rem 0;
  }
  ul {
    margin: 0;
    padding-left: 1.1rem;
    color: var(--text-secondary);
    font-size: 0.9rem;
    line-height: 1.7;
  }
`;

export const AccordionRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 720px;
`;

export const AccordionItem = styled.div`
  background: var(--surface-glass);
  border: 1px solid var(--border-subtle);
  border-radius: 14px;
  overflow: hidden;
`;

export const AccordionHead = styled.button`
  width: 100%;
  text-align: left;
  padding: 1.1rem 1.25rem;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: inherit;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

export const AccordionPanel = styled.div`
  padding: 0 1.25rem 1.25rem;
  color: var(--text-secondary);
  font-size: 0.9375rem;
  line-height: 1.72;
  border-top: 1px solid var(--border-subtle);
  p {
    margin: 0 0 0.75rem;
    color: var(--text-primary);
    font-weight: 500;
  }
  p:last-child {
    margin-bottom: 0;
  }
  ul {
    margin: 0.5rem 0 0;
    padding-left: 1.2rem;
    color: var(--text-secondary);
  }
  li {
    margin-bottom: 0.4rem;
    padding-left: 0.15rem;
    &::marker {
      color: var(--info);
    }
  }
`;

/** Mesma tipografia do H2 da seção (ex.: Conformidade e governança). */
export const GovernanceTeamHeading = styled(H2)`
  margin: 2rem 0 0.75rem;
  &:first-of-type {
    margin-top: 1.75rem;
  }
`;

export const GovernanceTeamsBlock = styled.div`
  margin-top: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.9375rem;
  line-height: 1.72;
  ul {
    margin: 0;
    padding-left: 1.2rem;
    color: var(--text-secondary);
  }
  li {
    margin-bottom: 0.4rem;
    padding-left: 0.15rem;
    &::marker {
      color: var(--info);
    }
  }
`;

export const GovernanceListLink = styled.a`
  color: inherit;
  text-decoration: none;
  font-weight: inherit;
  cursor: pointer;
  &:hover {
    color: var(--info);
  }
  &:focus-visible {
    outline: 2px solid var(--info);
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

export const DocList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const DocRow = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: var(--surface-glass);
  border-radius: 12px;
  border: 1px solid var(--border-subtle);
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 480px;
`;

export const Input = styled.input`
  padding: 0.85rem 1rem;
  border-radius: 12px;
  border: 1px solid var(--border-default);
  background: var(--input-bg);
  color: var(--text-primary);
  font-size: 1rem;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
  &::placeholder {
    color: var(--input-placeholder);
  }
`;

export const TextArea = styled.textarea`
  padding: 0.85rem 1rem;
  border-radius: 12px;
  border: 1px solid var(--border-default);
  background: var(--input-bg);
  color: var(--text-primary);
  font-size: 1rem;
  font-family: inherit;
  min-height: 120px;
  resize: vertical;
  width: 100%;
  box-sizing: border-box;
`;

export const FormError = styled.p`
  margin: -0.35rem 0 0;
  font-size: 0.8125rem;
  color: var(--danger);
`;

export const ContactSuccessBox = styled.div`
  max-width: 480px;
  padding: 1.5rem 1.25rem;
  border-radius: 16px;
  border: 1px solid var(--border-default);
  background: var(--accent-soft);
  color: var(--text-primary);

  h3 {
    margin: 0 0 0.5rem;
    font-size: 1.125rem;
    color: var(--accent);
  }

  p {
    margin: 0;
    line-height: 1.6;
    color: var(--text-secondary);
  }
`;

export const FooterBar = styled.footer`
  padding: 2rem 1.5rem;
  text-align: center;
  color: var(--text-muted);
  font-size: 0.8125rem;
  border-top: 1px solid var(--border-subtle);
`;

export const CertPreview = styled.div`
  width: 100%;
  max-width: 360px;
  margin-inline: auto;
  aspect-ratio: 3 / 4;
  max-height: min(420px, 52vh);
  overflow: hidden;
  border-radius: 16px;
  background: linear-gradient(
      135deg,
      rgba(37, 208, 127, 0.08),
      rgba(78, 216, 255, 0.06)
    ),
    var(--surface-1);
  border: 1px dashed var(--border-default);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  font-size: 0.9rem;
  padding: 1rem;
  text-align: center;
`;

/** Moldura para marcar áreas que exigem arquivo ou link externo (review de conteúdo). */
export const ExternalAssetFrame = styled.div`
  position: relative;
  border-radius: ${(props) => props.$radius ?? "16px"};
  outline: 2px dashed #e53935;
  outline-offset: 3px;
  background: rgba(229, 57, 53, 0.08);
  padding-top: ${(props) => props.$padTop ?? "2.35rem"};
`;

export const ExternalAssetRibbon = styled.span`
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  z-index: 3;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #ffebee;
  background: rgba(198, 40, 40, 0.95);
  padding: 0.4rem 0.55rem;
  border-radius: 6px;
  line-height: 1.35;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  white-space: normal;
  a {
    color: #ffe082;
    text-decoration: underline;
    font-weight: 700;
    word-break: break-all;
  }
`;

export const ContactNoticeBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2500;
  background: var(--surface-overlay);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
`;

export const ContactNoticePanel = styled.div`
  max-width: 440px;
  width: 100%;
  padding: 1.75rem 1.5rem;
  border-radius: 20px;
  background: linear-gradient(
      145deg,
      rgba(37, 208, 127, 0.14) 0%,
      rgba(16, 183, 166, 0.09) 45%,
      rgba(78, 216, 255, 0.11) 100%
    ),
    var(--surface-1);
  border: 1px solid var(--border-default);
  box-shadow: var(--shadow-lg);
`;

export const ContactNoticeTitle = styled.h3`
  margin: 0 0 0.75rem 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.02em;
`;

export const ContactNoticeText = styled.p`
  margin: 0 0 1.35rem 0;
  font-size: 0.9375rem;
  line-height: 1.65;
  color: var(--text-secondary);
`;
